export type {
  BetType, GameMode, MatchFormat,
  Player, Team, HoleInfo, TeeBox, CourseData,
  HoleScore, Press, NassauStatus,
  GameState, CourseSetup, BetStakes, SetupConfig,
  Leg, HoleResult, HoleResultMap,
  Bet, Stakes, LegSettlement, FullSettlement,
  Hole18ScenarioResult, Hole18ScenariosData,
  ClosedBetSummary, OpenBetGroup, OpenBetsByLeg, OpenBetNetSummary,
  BetStatusData, BackendRoundResponse,
  ManualGameEvent, HoleScoreInput, RoundConfig,
  RebuildRoundStateInput, RebuildRoundStateResult,
} from '@shared/gameTypes';

import type {
  CourseData, TeeBox, CourseSetup, Player, HoleInfo,
  HoleScore, Press, GameState, BetStakes,
  Team, HoleResultMap, Leg, Bet, Stakes,
  LegSettlement, FullSettlement, NassauStatus,
  BetType, MatchFormat, GameMode, BackendRoundResponse,
} from '@shared/gameTypes';

export function buildCourseSetup(courseData: CourseData, teeBox: TeeBox): CourseSetup {
  return {
    id: courseData.id,
    name: courseData.name,
    courseRating: teeBox.courseRating,
    slopeRating: teeBox.slopeRating,
    holes: courseData.pars.map((par, i) => ({
      number: i + 1,
      yardage: teeBox.yardages[i],
      strokeIndex: courseData.strokeIndexes[i],
      par,
    })),
  };
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export function getPlayingOrder(startingHole: 1 | 10): number[] {
  if (startingHole === 1) {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
  } else {
    return [10, 11, 12, 13, 14, 15, 16, 17, 18, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  }
}

export function getFrontHoles(startingHole: 1 | 10): number[] {
  if (startingHole === 1) {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9];
  } else {
    return [10, 11, 12, 13, 14, 15, 16, 17, 18];
  }
}

export function getBackHoles(startingHole: 1 | 10): number[] {
  if (startingHole === 1) {
    return [10, 11, 12, 13, 14, 15, 16, 17, 18];
  } else {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9];
  }
}

export function getPlayingPosition(holeNumber: number, startingHole: 1 | 10): number {
  const order = getPlayingOrder(startingHole);
  return order.indexOf(holeNumber) + 1;
}

export function isInFrontBet(holeNumber: number, startingHole: 1 | 10): boolean {
  return getFrontHoles(startingHole).includes(holeNumber);
}

export function isInBackBet(holeNumber: number, startingHole: 1 | 10): boolean {
  return getBackHoles(startingHole).includes(holeNumber);
}

export function calculateCourseHandicap(
  handicapIndex: number,
  slopeRating: number,
  courseRating: number,
  par: number
): number {
  const courseHcp = handicapIndex * (slopeRating / 113) + (courseRating - par);
  return Math.round(courseHcp);
}

export function calculateLowManHandicaps(
  players: Array<{ name: string; handicap: number; teamIndex: number; teeBoxId: string; isOrganizer?: boolean; personId?: string }>,
  courseData: CourseData
): Player[] {
  const totalPar = courseData.pars.reduce((sum, p) => sum + p, 0);
  
  const playersWithCourseHcp = players.map(p => {
    const teeBox = courseData.teeBoxes.find(t => t.id === p.teeBoxId) || courseData.teeBoxes[0];
    const courseHcp = calculateCourseHandicap(
      p.handicap,
      teeBox.slopeRating,
      teeBox.courseRating,
      totalPar
    );
    return { ...p, courseHcp, teeBox };
  });
  
  const minCourseHcp = Math.min(...playersWithCourseHcp.map(p => p.courseHcp));
  
  return playersWithCourseHcp.map(p => ({
    id: generateId(),
    name: p.name,
    handicap: p.handicap,
    courseHandicap: p.courseHcp,
    strokesReceived: Math.max(0, p.courseHcp - minCourseHcp),
    teeBoxId: p.teeBoxId,
    isOrganizer: p.isOrganizer,
    personId: p.personId,
  }));
}

export function playerReceivesStrokeOnHole(player: Player, hole: HoleInfo): boolean {
  return player.strokesReceived >= hole.strokeIndex;
}

export function getStrokesOnHole(player: Player, hole: HoleInfo): number {
  if (player.strokesReceived < hole.strokeIndex) return 0;
  return 1 + Math.floor((player.strokesReceived - hole.strokeIndex) / 18);
}

export function calculateNetScore(grossScore: number, receivesStroke: boolean, strokeCount: number = 1): number {
  return receivesStroke ? grossScore - strokeCount : grossScore;
}

export function getTeamBestBallNetForHole(
  team: Team,
  holeNumber: number,
  scores: HoleScore[],
  holes: HoleInfo[]
): number | null {
  const hole = holes.find(h => h.number === holeNumber);
  if (!hole) return null;

  const teamScores = team.players
    .map(player => {
      const score = scores.find(s => s.playerId === player.id && s.holeNumber === holeNumber);
      return score?.netScore ?? null;
    })
    .filter((s): s is number => s !== null);

  if (teamScores.length === 0) return null;
  return Math.min(...teamScores);
}

export function calculateHoleWinner(
  topTeam: Team,
  bottomTeam: Team,
  holeNumber: number,
  scores: HoleScore[],
  holes: HoleInfo[]
): number {
  const topBestBall = getTeamBestBallNetForHole(topTeam, holeNumber, scores, holes);
  const bottomBestBall = getTeamBestBallNetForHole(bottomTeam, holeNumber, scores, holes);

  if (topBestBall === null || bottomBestBall === null) return 0;
  if (topBestBall < bottomBestBall) return 1;
  if (topBestBall > bottomBestBall) return -1;
  return 0;
}

export function calculateBetStatus(
  topTeam: Team,
  bottomTeam: Team,
  scores: HoleScore[],
  holes: HoleInfo[],
  startHole: number,
  endHole: number
): number {
  let status = 0;
  
  for (let h = startHole; h <= endHole; h++) {
    const holeResult = calculateHoleWinner(topTeam, bottomTeam, h, scores, holes);
    status += holeResult;
  }
  
  return status;
}

export function calculateBetStatusForHoles(
  topTeam: Team,
  bottomTeam: Team,
  scores: HoleScore[],
  holes: HoleInfo[],
  holeNumbers: number[]
): number {
  let status = 0;
  
  for (const h of holeNumbers) {
    const holeResult = calculateHoleWinner(topTeam, bottomTeam, h, scores, holes);
    status += holeResult;
  }
  
  return status;
}

export function getRunningStatusAtEachHole(
  state: GameState,
  startHole: number,
  endHole: number
): Map<number, number> {
  const topTeam = state.teams[0];
  const bottomTeam = state.teams[1];
  const statusMap = new Map<number, number>();
  let runningStatus = 0;
  
  const lastPlayedHole = getLastPlayedHole(state);

  for (let h = startHole; h <= endHole; h++) {
    if (h > lastPlayedHole) break;
    
    const holeResult = calculateHoleWinner(topTeam, bottomTeam, h, state.scores, state.holes);
    runningStatus += holeResult;
    statusMap.set(h, runningStatus);
  }

  return statusMap;
}

function teamHasScoreOnHole(state: GameState, teamIndex: number, holeNumber: number): boolean {
  const teamPlayers = state.teams[teamIndex].players;
  return teamPlayers.some(player => {
    const score = state.scores.find(s => s.playerId === player.id && s.holeNumber === holeNumber);
    return score && score.grossScore !== null;
  });
}

export function getCompletedHolesInOrder(state: GameState): number[] {
  const playingOrder = getPlayingOrder(state.startingHole);
  const completedHoles: number[] = [];
  
  for (const holeNum of playingOrder) {
    const team0HasScore = teamHasScoreOnHole(state, 0, holeNum);
    const team1HasScore = teamHasScoreOnHole(state, 1, holeNum);
    
    if (team0HasScore && team1HasScore) {
      completedHoles.push(holeNum);
    } else {
      break;
    }
  }
  
  return completedHoles;
}

export function getLastPlayedHole(state: GameState): number {
  const completedHoles = getCompletedHolesInOrder(state);
  if (completedHoles.length === 0) return 0;
  return completedHoles[completedHoles.length - 1];
}

export function getHolesPlayedCount(state: GameState): number {
  return getCompletedHolesInOrder(state).length;
}

export function formatStatus(status: number): string {
  if (status === 0) return 'E';
  if (status > 0) return `+${status}`;
  return `${status}`;
}

export function formatPressStatus(status: number, stackCount: number): string {
  const statusStr = formatStatus(status);
  return `${statusStr}${stackCount > 1 ? `×${stackCount}` : 'P'}`;
}

export function isRoundComplete(state: GameState): boolean {
  const allPlayers = [...state.teams[0].players, ...state.teams[1].players];
  
  for (let holeNum = 1; holeNum <= 18; holeNum++) {
    for (const player of allPlayers) {
      const score = state.scores.find(s => s.playerId === player.id && s.holeNumber === holeNum);
      if (!score || score.grossScore === null) {
        return false;
      }
    }
  }
  return true;
}

export function finalizeRound(state: GameState): GameState {
  return {
    ...state,
    isFinalized: true,
  };
}

export function unfinalizeRound(state: GameState): GameState {
  return {
    ...state,
    isFinalized: false,
  };
}

export function buildHoleResultMap(state: GameState): HoleResultMap {
  const topTeam = state.teams[0];
  const bottomTeam = state.teams[1];
  const results: HoleResultMap = {};
  
  for (let h = 1; h <= 18; h++) {
    const holeResult = calculateHoleWinner(topTeam, bottomTeam, h, state.scores, state.holes);
    if (holeResult > 0) {
      results[h] = 'A';
    } else if (holeResult < 0) {
      results[h] = 'B';
    } else {
      results[h] = 'Tie';
    }
  }
  
  return results;
}

export function getLegEndHole(leg: Leg, startingHole: 1 | 10): number {
  if (leg === 'Front') {
    return startingHole === 1 ? 9 : 18;
  } else if (leg === 'Back') {
    return startingHole === 1 ? 18 : 9;
  } else {
    return startingHole === 1 ? 18 : 9;
  }
}

export function getLegStartHole(leg: Leg, startingHole: 1 | 10): number {
  if (leg === 'Front') {
    return startingHole === 1 ? 1 : 10;
  } else if (leg === 'Back') {
    return startingHole === 1 ? 10 : 1;
  } else {
    return startingHole;
  }
}

export function getLegHoles(leg: Leg, startingHole: 1 | 10): number[] {
  if (leg === 'Front') {
    return getFrontHoles(startingHole);
  } else if (leg === 'Back') {
    return getBackHoles(startingHole);
  } else {
    return getPlayingOrder(startingHole);
  }
}

export function getMaxPressIndexOnLeg(leg: Leg, bets: Bet[]): number {
  const legBets = bets.filter(b => b.leg === leg);
  if (legBets.length === 0) return -1;
  return Math.max(...legBets.map(b => b.pressIndex));
}

export function whoIsDownOnBet(
  bet: Bet,
  currentHole: number,
  holeResults: HoleResultMap,
  startingHole: 1 | 10
): 'A' | 'B' | null {
  const legHoles = getLegHoles(bet.leg, startingHole);
  const playingOrder = getPlayingOrder(startingHole);
  const currentPos = playingOrder.indexOf(currentHole);
  
  let aUp = 0;
  for (const h of legHoles) {
    const holePos = playingOrder.indexOf(h);
    if (holePos >= playingOrder.indexOf(bet.startHole) && holePos < currentPos) {
      const r = holeResults[h];
      if (r === 'A') aUp++;
      else if (r === 'B') aUp--;
    }
  }
  
  if (aUp > 0) return 'B';
  if (aUp < 0) return 'A';
  return null;
}

export function createBaseBets(startingHole: 1 | 10): Bet[] {
  const legs: Leg[] = ['Front', 'Back', 'Overall'];
  return legs.map(leg => ({
    id: generateId(),
    leg,
    pressIndex: 0,
    unitIndex: 0,
    startHole: getLegStartHole(leg, startingHole),
    endHole: getLegEndHole(leg, startingHole),
    winner: null,
  }));
}

export function canSidePress(
  leg: Leg,
  basePressIndex: number,
  side: 'A' | 'B',
  currentHole: number,
  holeResults: HoleResultMap,
  startingHole: 1 | 10,
  existingBets: Bet[]
): boolean {
  const betToPress = existingBets.find(b => b.leg === leg && b.pressIndex === basePressIndex);
  if (!betToPress) return false;
  
  const legHoles = getLegHoles(leg, startingHole);
  const playingOrder = getPlayingOrder(startingHole);
  const betStartPos = playingOrder.indexOf(betToPress.startHole);
  const currentPos = playingOrder.indexOf(currentHole);
  
  let aUp = 0;
  for (const h of legHoles) {
    const holePos = playingOrder.indexOf(h);
    if (holePos >= betStartPos && holePos <= currentPos) {
      const r = holeResults[h];
      if (r === 'A') aUp++;
      else if (r === 'B') aUp--;
    }
  }
  
  if (side === 'A') return aUp < 0;
  return aUp > 0;
}

export function createPresses(
  leg: Leg,
  basePressIndex: number,
  units: number,
  startHole: number,
  startingHole: 1 | 10,
  holeResults: HoleResultMap,
  existingBets: Bet[],
  pressingSide: 'A' | 'B'
): Bet[] {
  if (units <= 0) return [];
  
  if (!canSidePress(leg, basePressIndex, pressingSide, startHole, holeResults, startingHole, existingBets)) {
    return [];
  }
  
  const endHole = getLegEndHole(leg, startingHole);
  const nextPressIndex = getMaxPressIndexOnLeg(leg, existingBets) + 1;
  
  const newBets: Bet[] = [];
  for (let i = 0; i < units; i++) {
    newBets.push({
      id: generateId(),
      leg,
      pressIndex: nextPressIndex,
      unitIndex: i,
      startHole,
      endHole,
      winner: null,
    });
  }
  
  return newBets;
}

export function evaluateBet(bet: Bet, holes: HoleResultMap, startingHole: 1 | 10): 'A' | 'B' | 'Tie' {
  const legHoles = getLegHoles(bet.leg, startingHole);
  const playingOrder = getPlayingOrder(startingHole);
  const betStartPos = playingOrder.indexOf(bet.startHole);
  const betEndPos = playingOrder.indexOf(bet.endHole);
  
  const isLastHolePress = bet.pressIndex > 0 && betStartPos === betEndPos;
  const effectiveStartPos = bet.pressIndex > 0 && !isLastHolePress ? betStartPos + 1 : betStartPos;
  
  let aUp = 0;
  for (const h of legHoles) {
    const holePos = playingOrder.indexOf(h);
    if (holePos >= effectiveStartPos && holePos <= betEndPos) {
      const r = holes[h];
      if (r === 'A') aUp++;
      else if (r === 'B') aUp--;
    }
  }
  
  if (aUp > 0) return 'A';
  if (aUp < 0) return 'B';
  return 'Tie';
}

export function settleLeg(
  leg: Leg,
  bets: Bet[],
  stakes: Stakes
): LegSettlement {
  const legStake = stakes[leg];
  const betsOnLeg = bets.filter(b => b.leg === leg);
  
  let unitsForA = 0;
  for (const b of betsOnLeg) {
    if (b.winner === 'A') unitsForA += 1;
    else if (b.winner === 'B') unitsForA -= 1;
  }
  
  const amountForA = unitsForA * legStake;
  return { unitsForA, amountForA };
}

export function settleAll(bets: Bet[], stakes: Stakes): FullSettlement {
  const front = settleLeg('Front', bets, stakes);
  const back = settleLeg('Back', bets, stakes);
  const overall = settleLeg('Overall', bets, stakes);
  
  const totalAmountForA = front.amountForA + back.amountForA + overall.amountForA;
  
  return {
    perLeg: {
      Front: front,
      Back: back,
      Overall: overall,
    },
    totalAmountForA,
    totalAmountForB: -totalAmountForA,
  };
}

export function evaluateAllBets(bets: Bet[], holes: HoleResultMap, startingHole: 1 | 10): Bet[] {
  return bets.map(bet => ({
    ...bet,
    winner: evaluateBet(bet, holes, startingHole),
  }));
}

export function convertPressesToBets(state: GameState): Bet[] {
  const bets: Bet[] = [];
  
  const legs: Leg[] = ['Front', 'Back', 'Overall'];
  for (const leg of legs) {
    bets.push({
      id: `base-${leg}`,
      leg,
      pressIndex: 0,
      unitIndex: 0,
      startHole: getLegStartHole(leg, state.startingHole),
      endHole: getLegEndHole(leg, state.startingHole),
      winner: null,
    });
  }
  
  const playingOrder = getPlayingOrder(state.startingHole);
  const pressesByLeg: Record<Leg, Press[]> = { Front: [], Back: [], Overall: [] };
  
  for (const press of state.presses) {
    const leg = press.betType as Leg;
    pressesByLeg[leg].push(press);
  }
  
  for (const leg of legs) {
    const legPresses = pressesByLeg[leg].sort((a, b) => 
      playingOrder.indexOf(a.startingHole) - playingOrder.indexOf(b.startingHole)
    );
    
    let pressIndex = 1;
    for (const press of legPresses) {
      for (let u = 0; u < press.stackCount; u++) {
        bets.push({
          id: `${press.id}-${u}`,
          leg,
          pressIndex,
          unitIndex: u,
          startHole: press.startingHole,
          endHole: getLegEndHole(leg, state.startingHole),
          winner: null,
        });
      }
      pressIndex++;
    }
  }
  
  return bets;
}

export function convertToStakes(betStakes: BetStakes): Stakes {
  return {
    Front: betStakes.front,
    Back: betStakes.back,
    Overall: betStakes.overall,
  };
}

export const INTERNATIONAL_CC: CourseData = {
  id: 'international-cc',
  name: 'International Country Club',
  location: 'Fairfax, VA',
  pars: [5, 4, 4, 3, 4, 4, 4, 3, 4, 4, 3, 4, 4, 4, 5, 5, 3, 4],
  strokeIndexes: [13, 11, 7, 15, 9, 1, 3, 17, 5, 8, 18, 14, 4, 12, 6, 2, 16, 10],
  teeBoxes: [
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 73.4, slopeRating: 140,
      yardages: [471, 387, 375, 219, 353, 430, 448, 183, 431, 404, 163, 372, 406, 422, 558, 601, 190, 421],
    },
    {
      id: 'gold-black', name: 'Gold/Black', color: '#B8960B', gender: 'M',
      courseRating: 72.4, slopeRating: 138,
      yardages: [456, 375, 363, 212, 342, 417, 434, 177, 418, 391, 158, 360, 393, 409, 542, 583, 184, 408],
    },
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 71.7, slopeRating: 136,
      yardages: [444, 365, 354, 206, 333, 405, 422, 173, 406, 381, 154, 351, 383, 398, 526, 566, 179, 397],
    },
    {
      id: 'black-silver', name: 'Black/Silver', color: '#606060', gender: 'M',
      courseRating: 70.2, slopeRating: 134,
      yardages: [428, 351, 340, 199, 321, 390, 407, 166, 391, 367, 148, 338, 369, 383, 507, 545, 173, 382],
    },
    {
      id: 'silver', name: 'Silver', color: '#C0C0C0', gender: 'M',
      courseRating: 69.5, slopeRating: 132,
      yardages: [414, 341, 330, 193, 311, 379, 395, 161, 380, 356, 144, 328, 358, 372, 490, 528, 167, 371],
    },
    {
      id: 'silver-blue', name: 'Silver/Blue', color: '#6699CC', gender: 'M',
      courseRating: 68.9, slopeRating: 130,
      yardages: [404, 332, 322, 188, 303, 369, 384, 157, 370, 347, 140, 319, 348, 362, 479, 515, 163, 361],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 68.2, slopeRating: 126,
      yardages: [391, 322, 312, 182, 293, 357, 372, 152, 358, 336, 135, 309, 337, 351, 464, 501, 158, 350],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 65.7, slopeRating: 120,
      yardages: [354, 291, 282, 165, 266, 324, 337, 138, 324, 304, 123, 280, 305, 318, 420, 451, 143, 317],
    },
    {
      id: 'silver-w', name: 'Silver (Women)', color: '#C0C0C0', gender: 'F',
      courseRating: 75.1, slopeRating: 133,
      yardages: [450, 352, 346, 192, 335, 380, 370, 159, 350, 375, 141, 350, 329, 381, 477, 511, 174, 374],
    },
    {
      id: 'blue-w', name: 'Blue (Women)', color: '#0066CC', gender: 'F',
      courseRating: 73.6, slopeRating: 125,
      yardages: [437, 340, 333, 183, 324, 373, 332, 147, 325, 367, 125, 327, 324, 373, 429, 457, 167, 361],
    },
    {
      id: 'white-w', name: 'White (Women)', color: '#FFFFFF', gender: 'F',
      courseRating: 70.5, slopeRating: 129,
      yardages: [354, 291, 282, 165, 266, 324, 337, 138, 324, 304, 123, 280, 305, 318, 420, 451, 143, 317],
    },
  ],
};

export const EAGLE_MOUNTAIN: CourseData = {
  id: 'eagle-mountain',
  name: 'Eagle Mountain Golf Club',
  location: 'Fountain Hills, AZ',
  pars: [4, 5, 3, 4, 4, 4, 3, 5, 4, 4, 4, 3, 5, 4, 4, 3, 4, 5],
  strokeIndexes: [7, 3, 15, 1, 11, 5, 17, 9, 13, 6, 2, 16, 8, 12, 4, 18, 10, 14],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 72.8, slopeRating: 137,
      yardages: [412, 545, 178, 468, 385, 428, 205, 562, 378, 405, 445, 165, 548, 388, 430, 155, 395, 535],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 70.5, slopeRating: 131,
      yardages: [385, 518, 162, 442, 358, 402, 185, 535, 355, 380, 418, 148, 520, 362, 405, 140, 368, 508],
    },
    {
      id: 'green', name: 'Green', color: '#228B22', gender: 'M',
      courseRating: 68.2, slopeRating: 124,
      yardages: [358, 490, 145, 415, 332, 375, 165, 505, 330, 355, 390, 132, 492, 338, 378, 125, 342, 480],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 65.8, slopeRating: 118,
      yardages: [330, 462, 128, 388, 305, 348, 148, 478, 305, 328, 362, 115, 465, 312, 352, 110, 315, 452],
    },
    {
      id: 'red', name: 'Red', color: '#FF0000', gender: 'F',
      courseRating: 70.2, slopeRating: 125,
      yardages: [305, 435, 112, 360, 280, 322, 130, 450, 280, 302, 335, 100, 438, 288, 325, 95, 290, 425],
    },
  ],
};

export const WE_KO_PA: CourseData = {
  id: 'we-ko-pa',
  name: 'We-Ko-Pa Golf Club',
  location: 'Fort McDowell, AZ',
  pars: [4, 4, 5, 3, 4, 4, 3, 5, 4, 4, 3, 4, 5, 4, 4, 3, 5, 4],
  strokeIndexes: [9, 5, 1, 17, 7, 13, 15, 3, 11, 4, 18, 10, 2, 8, 12, 16, 6, 14],
  teeBoxes: [
    {
      id: 'championship', name: 'Championship', color: '#000000', gender: 'M',
      courseRating: 74.2, slopeRating: 145,
      yardages: [445, 420, 585, 195, 438, 392, 210, 575, 425, 418, 178, 405, 565, 430, 388, 175, 548, 405],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 71.8, slopeRating: 138,
      yardages: [418, 395, 555, 175, 412, 368, 188, 545, 398, 392, 160, 378, 535, 405, 362, 158, 520, 378],
    },
    {
      id: 'purple', name: 'Purple', color: '#800080', gender: 'M',
      courseRating: 69.5, slopeRating: 130,
      yardages: [390, 368, 525, 158, 385, 342, 168, 515, 372, 365, 142, 352, 505, 378, 338, 142, 492, 352],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 66.8, slopeRating: 122,
      yardages: [362, 342, 495, 140, 358, 318, 148, 485, 345, 338, 125, 325, 475, 352, 312, 125, 465, 325],
    },
    {
      id: 'red', name: 'Red', color: '#FF0000', gender: 'F',
      courseRating: 71.5, slopeRating: 128,
      yardages: [335, 315, 465, 122, 330, 292, 130, 455, 318, 312, 108, 298, 445, 325, 288, 110, 438, 298],
    },
  ],
};

export const SILVERLEAF: CourseData = {
  id: 'silverleaf',
  name: 'Silverleaf Golf Club',
  location: 'Scottsdale, AZ',
  pars: [4, 4, 5, 4, 3, 4, 3, 5, 4, 4, 4, 4, 4, 5, 4, 3, 4, 4],
  strokeIndexes: [9, 11, 3, 13, 17, 5, 15, 1, 7, 2, 12, 8, 10, 4, 6, 18, 14, 16],
  teeBoxes: [
    {
      id: 'silver', name: 'Silver', color: '#C0C0C0', gender: 'M',
      courseRating: 74.8, slopeRating: 146,
      yardages: [419, 406, 544, 375, 254, 423, 217, 569, 313, 477, 388, 418, 418, 576, 427, 234, 436, 498],
    },
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 72.3, slopeRating: 140,
      yardages: [384, 375, 518, 355, 206, 399, 194, 540, 294, 441, 359, 390, 395, 540, 392, 209, 415, 466],
    },
    {
      id: 'black-blue', name: 'Black/Blue', color: '#1a1a2e', gender: 'M',
      courseRating: 71.5, slopeRating: 138,
      yardages: [384, 375, 518, 355, 183, 399, 178, 504, 294, 429, 359, 354, 364, 540, 373, 175, 415, 445],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 70.0, slopeRating: 135,
      yardages: [357, 348, 506, 296, 183, 360, 178, 504, 271, 429, 315, 354, 364, 521, 373, 175, 384, 445],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 67.7, slopeRating: 127,
      yardages: [352, 317, 484, 278, 164, 326, 156, 467, 255, 393, 284, 335, 328, 486, 318, 158, 351, 359],
    },
    {
      id: 'red', name: 'Red', color: '#FF0000', gender: 'F',
      courseRating: 70.2, slopeRating: 125,
      yardages: [305, 277, 436, 252, 144, 285, 122, 385, 240, 330, 261, 308, 256, 465, 266, 103, 305, 338],
    },
  ],
};

export const BLUE_MASH: CourseData = {
  id: 'blue-mash',
  name: 'Blue Mash Golf Course',
  location: 'Laytonsville, MD',
  pars: [4, 4, 4, 3, 5, 3, 4, 4, 5, 4, 3, 5, 3, 4, 4, 4, 3, 5],
  strokeIndexes: [3, 9, 1, 13, 5, 17, 11, 15, 7, 10, 16, 2, 18, 12, 6, 4, 14, 8],
  teeBoxes: [
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 71.2, slopeRating: 126,
      yardages: [449, 452, 478, 206, 580, 160, 392, 310, 548, 398, 217, 500, 177, 378, 423, 463, 192, 562],
    },
  ],
};

export const SHEEP_RANCH: CourseData = {
  id: 'sheep-ranch',
  name: "Sheep Ranch at Bandon Dunes",
  location: 'Bandon, OR',
  pars: [5, 4, 3, 4, 3, 4, 3, 4, 4, 4, 5, 4, 5, 4, 4, 3, 4, 5],
  strokeIndexes: [5, 13, 17, 3, 11, 1, 15, 7, 9, 6, 4, 2, 10, 8, 14, 16, 12, 18],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 71.9, slopeRating: 121,
      yardages: [549, 318, 120, 474, 198, 460, 155, 429, 399, 390, 529, 440, 510, 403, 321, 151, 326, 464],
    },
    {
      id: 'green', name: 'Green', color: '#228B22', gender: 'M',
      courseRating: 70.0, slopeRating: 116,
      yardages: [517, 299, 113, 446, 186, 433, 146, 404, 375, 367, 498, 414, 480, 379, 302, 142, 307, 437],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 67.9, slopeRating: 109,
      yardages: [482, 278, 105, 415, 173, 403, 136, 376, 349, 341, 463, 385, 447, 353, 281, 132, 285, 406],
    },
    {
      id: 'orange', name: 'Orange', color: '#FF8C00', gender: 'M',
      courseRating: 65.0, slopeRating: 102,
      yardages: [426, 247, 93, 367, 153, 357, 120, 333, 309, 302, 410, 341, 395, 312, 249, 117, 253, 360],
    },
    {
      id: 'royal-blue', name: 'Royal Blue', color: '#4169E1', gender: 'M',
      courseRating: 61.0, slopeRating: 97,
      yardages: [326, 189, 71, 282, 118, 273, 92, 255, 237, 232, 314, 261, 303, 239, 191, 90, 194, 276],
    },
    {
      id: 'black-w', name: 'Black (Women)', color: '#000000', gender: 'F',
      courseRating: 77.2, slopeRating: 137,
      yardages: [549, 318, 120, 474, 198, 460, 155, 429, 399, 390, 529, 440, 510, 403, 321, 151, 326, 464],
    },
    {
      id: 'green-w', name: 'Green (Women)', color: '#228B22', gender: 'F',
      courseRating: 75.1, slopeRating: 132,
      yardages: [517, 299, 113, 446, 186, 433, 146, 404, 375, 367, 498, 414, 480, 379, 302, 142, 307, 437],
    },
    {
      id: 'gold-w', name: 'Gold (Women)', color: '#FFD700', gender: 'F',
      courseRating: 74.1, slopeRating: 124,
      yardages: [482, 278, 105, 415, 173, 403, 136, 376, 349, 341, 463, 385, 447, 353, 281, 132, 285, 406],
    },
    {
      id: 'orange-w', name: 'Orange (Women)', color: '#FF8C00', gender: 'F',
      courseRating: 70.1, slopeRating: 112,
      yardages: [426, 247, 93, 367, 153, 357, 120, 333, 309, 302, 410, 341, 395, 312, 249, 117, 253, 360],
    },
    {
      id: 'royal-blue-w', name: 'Royal Blue (Women)', color: '#4169E1', gender: 'F',
      courseRating: 62.2, slopeRating: 95,
      yardages: [326, 189, 71, 282, 118, 273, 92, 255, 237, 232, 314, 261, 303, 239, 191, 90, 194, 276],
    },
  ],
};

export const RTJ_GOLF_CLUB: CourseData = {
  id: 'rtj-golf-club',
  name: 'Robert Trent Jones Golf Club',
  location: 'Gainesville, VA',
  pars: [4, 4, 4, 3, 5, 4, 4, 5, 3, 4, 3, 5, 4, 5, 4, 3, 4, 4],
  // Stroke indexes from physical scorecard (Aug 2026)
  // Front: 3,7,1,17,11,13,9,5,15  Back: 10,14,16,4,8,2,18,12,6
  strokeIndexes: [3, 7, 1, 17, 11, 13, 9, 5, 15, 10, 14, 16, 4, 8, 2, 18, 12, 6],
  teeBoxes: [
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 76.9, slopeRating: 148,
      // Front: 475,430,492,207,557,432,442,583,203=3821  Back: 381,216,532,472,619,470,180,377,477=3724  Tot:7545
      yardages: [475, 430, 492, 207, 557, 432, 442, 583, 203, 381, 216, 532, 472, 619, 470, 180, 377, 477],
    },
    {
      id: 'green', name: 'Green', color: '#228B22', gender: 'M',
      courseRating: 74.8, slopeRating: 144,
      // Front: 450,400,470,180,535,400,410,550,195=3590  Back: 370,190,505,450,545,440,160,365,450=3475  Tot:7065
      yardages: [450, 400, 470, 180, 535, 400, 410, 550, 195, 370, 190, 505, 450, 545, 440, 160, 365, 450],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 73.0, slopeRating: 140,
      // Front: 430,385,445,155,510,370,390,515,170=3370  Back: 350,165,475,420,505,415,145,345,425=3245  Tot:6615
      yardages: [430, 385, 445, 155, 510, 370, 390, 515, 170, 350, 165, 475, 420, 505, 415, 145, 345, 425],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 71.0, slopeRating: 135,
      // Front: 410,360,415,130,490,355,370,490,160=3180  Back: 315,145,460,395,470,385,125,320,400=3015  Tot:6195
      yardages: [410, 360, 415, 130, 490, 355, 370, 490, 160, 315, 145, 460, 395, 470, 385, 125, 320, 400],
    },
    {
      id: 'black', name: 'Black', color: '#222222', gender: 'M',
      courseRating: 68.2, slopeRating: 130,
      // Front: 360,330,360,105,465,330,340,435,130=2855  Back: 285,125,420,355,425,330,105,270,365=2680  Tot:5535
      yardages: [360, 330, 360, 105, 465, 330, 340, 435, 130, 285, 125, 420, 355, 425, 330, 105, 270, 365],
    },
    {
      id: 'blue-w', name: 'Blue (Women)', color: '#0066CC', gender: 'F',
      courseRating: 77.2, slopeRating: 137,
      yardages: [430, 385, 445, 155, 510, 370, 390, 515, 170, 350, 165, 475, 420, 505, 415, 145, 345, 425],
    },
    {
      id: 'white-w', name: 'White (Women)', color: '#FFFFFF', gender: 'F',
      courseRating: 77.2, slopeRating: 142,
      yardages: [410, 360, 415, 130, 490, 355, 370, 490, 160, 315, 145, 460, 395, 470, 385, 125, 320, 400],
    },
    {
      id: 'black-w', name: 'Black (Women)', color: '#222222', gender: 'F',
      courseRating: 73.7, slopeRating: 135,
      yardages: [360, 330, 360, 105, 465, 330, 340, 435, 130, 285, 125, 420, 355, 425, 330, 105, 270, 365],
    },
  ],
};

export const OLD_MACDONALD: CourseData = {
  id: 'old-macdonald',
  name: 'Old Macdonald at Bandon Dunes',
  location: 'Bandon, OR',
  pars: [4, 3, 4, 4, 3, 5, 4, 3, 4, 4, 4, 3, 4, 4, 5, 4, 5, 4],
  strokeIndexes: [11, 15, 9, 1, 17, 3, 7, 13, 5, 6, 4, 16, 18, 14, 12, 2, 10, 8],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 74.1, slopeRating: 133,
      yardages: [341, 200, 375, 504, 175, 555, 363, 181, 416, 465, 445, 237, 346, 370, 535, 455, 546, 469],
    },
    {
      id: 'green', name: 'Green', color: '#228B22', gender: 'M',
      courseRating: 71.3, slopeRating: 127,
      yardages: [304, 181, 345, 472, 147, 520, 345, 170, 352, 440, 399, 205, 319, 297, 482, 433, 515, 426],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 68.2, slopeRating: 119,
      yardages: [275, 157, 312, 441, 135, 467, 322, 152, 343, 383, 390, 164, 253, 254, 424, 408, 397, 413],
    },
    {
      id: 'royal-blue', name: 'Royal Blue', color: '#4169E1', gender: 'M',
      courseRating: 62.6, slopeRating: 104,
      yardages: [213, 123, 217, 283, 115, 354, 195, 121, 231, 323, 313, 91, 220, 230, 326, 270, 343, 312],
    },
    {
      id: 'green-w', name: 'Green (Women)', color: '#228B22', gender: 'F',
      courseRating: 77.7, slopeRating: 136,
      yardages: [304, 181, 345, 472, 147, 520, 345, 170, 352, 440, 399, 205, 319, 297, 482, 433, 515, 426],
    },
    {
      id: 'gold-w', name: 'Gold (Women)', color: '#FFD700', gender: 'F',
      courseRating: 73.9, slopeRating: 129,
      yardages: [275, 157, 312, 441, 135, 467, 322, 152, 343, 383, 390, 164, 253, 254, 424, 408, 397, 413],
    },
    {
      id: 'orange-w', name: 'Orange (Women)', color: '#FF8C00', gender: 'F',
      courseRating: 70.4, slopeRating: 118,
      yardages: [242, 150, 244, 391, 115, 401, 311, 130, 284, 369, 344, 149, 247, 238, 340, 326, 391, 372],
    },
    {
      id: 'royal-blue-w', name: 'Royal Blue (Women)', color: '#4169E1', gender: 'F',
      courseRating: 66.4, slopeRating: 108,
      yardages: [213, 123, 217, 283, 115, 354, 195, 121, 231, 323, 313, 91, 220, 230, 326, 270, 343, 312],
    },
  ],
};

export const BANDON_TRAILS: CourseData = {
  id: 'bandon-trails',
  name: 'Bandon Trails at Bandon Dunes',
  location: 'Bandon, OR',
  pars: [4, 3, 5, 4, 3, 4, 4, 4, 5, 4, 4, 3, 4, 4, 4, 5, 3, 4],
  strokeIndexes: [13, 17, 3, 5, 15, 9, 7, 11, 1, 10, 4, 18, 12, 14, 8, 2, 16, 6],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 72.8, slopeRating: 132,
      yardages: [392, 214, 549, 408, 133, 395, 440, 321, 567, 418, 445, 242, 401, 325, 406, 530, 180, 399],
    },
    {
      id: 'green', name: 'Green', color: '#228B22', gender: 'M',
      courseRating: 70.6, slopeRating: 128,
      yardages: [356, 166, 532, 363, 124, 359, 406, 299, 522, 393, 429, 235, 374, 306, 367, 494, 163, 372],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 68.2, slopeRating: 123,
      yardages: [288, 142, 513, 334, 114, 315, 373, 283, 497, 369, 398, 220, 354, 292, 323, 466, 114, 336],
    },
    {
      id: 'green-w', name: 'Green (Women)', color: '#228B22', gender: 'F',
      courseRating: 76.9, slopeRating: 140,
      yardages: [356, 166, 532, 363, 124, 359, 406, 299, 522, 393, 429, 235, 374, 306, 367, 494, 163, 372],
    },
    {
      id: 'gold-w', name: 'Gold (Women)', color: '#FFD700', gender: 'F',
      courseRating: 73.9, slopeRating: 133,
      yardages: [288, 142, 513, 334, 114, 315, 373, 283, 497, 369, 398, 220, 354, 292, 323, 466, 114, 336],
    },
    {
      id: 'orange-w', name: 'Orange (Women)', color: '#FF8C00', gender: 'F',
      courseRating: 70.3, slopeRating: 126,
      yardages: [212, 84, 455, 315, 104, 293, 351, 243, 456, 326, 382, 192, 308, 251, 296, 437, 92, 267],
    },
  ],
};

export const PACIFIC_DUNES: CourseData = {
  id: 'pacific-dunes',
  name: 'Pacific Dunes at Bandon Dunes',
  location: 'Bandon, OR',
  pars: [4, 4, 5, 4, 3, 4, 4, 4, 4, 3, 3, 5, 4, 3, 5, 4, 3, 5],
  strokeIndexes: [9, 11, 7, 3, 17, 13, 1, 5, 15, 14, 18, 6, 2, 16, 10, 12, 8, 4],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 71.5, slopeRating: 129,
      yardages: [370, 368, 499, 463, 199, 316, 464, 400, 406, 206, 148, 529, 444, 145, 539, 338, 208, 591],
    },
    {
      id: 'green', name: 'Green', color: '#228B22', gender: 'M',
      courseRating: 69.3, slopeRating: 125,
      yardages: [304, 335, 476, 449, 181, 288, 436, 369, 379, 163, 131, 507, 390, 128, 504, 338, 189, 575],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 67.6, slopeRating: 122,
      yardages: [287, 335, 476, 410, 163, 288, 377, 349, 356, 149, 114, 476, 371, 119, 492, 301, 164, 548],
    },
    {
      id: 'gold-w', name: 'Gold (Women)', color: '#FFD700', gender: 'F',
      courseRating: 73.1, slopeRating: 126,
      yardages: [287, 335, 476, 410, 163, 288, 377, 349, 356, 149, 114, 476, 371, 119, 492, 301, 164, 548],
    },
    {
      id: 'orange-w', name: 'Orange (Women)', color: '#FF8C00', gender: 'F',
      courseRating: 69.3, slopeRating: 118,
      yardages: [253, 275, 452, 369, 133, 267, 314, 296, 268, 129, 96, 449, 336, 100, 452, 292, 128, 479],
    },
  ],
};

export const BANDON_DUNES: CourseData = {
  id: 'bandon-dunes',
  name: 'Bandon Dunes',
  location: 'Bandon, OR',
  pars: [4, 3, 5, 4, 4, 3, 4, 4, 5, 4, 4, 3, 5, 4, 3, 4, 4, 5],
  strokeIndexes: [13, 15, 3, 5, 1, 17, 7, 11, 9, 8, 2, 18, 6, 16, 14, 10, 12, 4],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 74.1, slopeRating: 143,
      yardages: [386, 189, 543, 410, 428, 161, 383, 359, 558, 362, 384, 199, 553, 359, 163, 363, 389, 543],
    },
    {
      id: 'green', name: 'Green', color: '#228B22', gender: 'M',
      courseRating: 71.7, slopeRating: 139,
      yardages: [352, 155, 489, 362, 400, 153, 372, 342, 520, 339, 351, 153, 537, 332, 131, 345, 375, 513],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 69.5, slopeRating: 133,
      yardages: [332, 136, 467, 340, 374, 126, 332, 321, 510, 302, 315, 129, 498, 320, 113, 301, 329, 471],
    },
    {
      id: 'gold-w', name: 'Gold (Women)', color: '#FFD700', gender: 'F',
      courseRating: 75.9, slopeRating: 136,
      yardages: [332, 136, 467, 340, 374, 126, 332, 321, 510, 302, 315, 129, 498, 320, 113, 301, 329, 471],
    },
    {
      id: 'orange-w', name: 'Orange (Women)', color: '#FF8C00', gender: 'F',
      courseRating: 72.4, slopeRating: 128,
      yardages: [293, 130, 370, 308, 321, 111, 317, 290, 464, 251, 284, 98, 447, 288, 102, 250, 324, 424],
    },
  ],
};

export const TPC_POTOMAC: CourseData = {
  id: 'tpc-potomac',
  name: 'TPC Potomac at Avenel Farm',
  location: 'Potomac, MD',
  pars: [4, 5, 3, 4, 4, 4, 4, 4, 3, 5, 4, 3, 4, 4, 5, 4, 3, 4],
  strokeIndexes: [3, 5, 13, 7, 17, 1, 11, 9, 15, 4, 2, 16, 14, 10, 18, 8, 12, 6],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 75.5, slopeRating: 146,
      yardages: [440, 619, 225, 440, 365, 484, 452, 467, 201, 560, 470, 168, 360, 299, 490, 412, 222, 465],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 73.5, slopeRating: 142,
      yardages: [415, 570, 210, 379, 336, 449, 420, 435, 175, 532, 420, 161, 324, 278, 440, 390, 190, 426],
    },
    {
      id: 'gold-silver', name: 'Gold-Silver Combo', color: '#C0C0C0', gender: 'M',
      courseRating: 71.7, slopeRating: 139,
      yardages: [400, 570, 185, 440, 336, 422, 383, 415, 155, 532, 378, 161, 360, 278, 490, 390, 170, 401],
    },
    {
      id: 'silver', name: 'Silver', color: '#A8A8A8', gender: 'M',
      courseRating: 70.6, slopeRating: 135,
      yardages: [400, 495, 185, 362, 312, 422, 395, 415, 155, 510, 378, 144, 302, 256, 418, 327, 170, 401],
    },
    {
      id: 'copper', name: 'Copper', color: '#B87333', gender: 'M',
      courseRating: 69.3, slopeRating: 128,
      yardages: [392, 478, 160, 352, 299, 379, 383, 390, 136, 482, 365, 144, 279, 239, 397, 319, 152, 380],
    },
    {
      id: 'ivory', name: 'Ivory', color: '#FFFFF0', gender: 'M',
      courseRating: 65.2, slopeRating: 121,
      yardages: [301, 455, 135, 335, 275, 360, 266, 275, 120, 438, 339, 130, 258, 225, 353, 305, 115, 340],
    },
    {
      id: 'silver-w', name: 'Silver (Women)', color: '#A8A8A8', gender: 'F',
      courseRating: 76.9, slopeRating: 149,
      yardages: [400, 495, 185, 362, 312, 422, 395, 415, 155, 510, 378, 144, 302, 256, 418, 327, 170, 401],
    },
    {
      id: 'copper-w', name: 'Copper (Women)', color: '#B87333', gender: 'F',
      courseRating: 74.9, slopeRating: 146,
      yardages: [392, 478, 160, 352, 299, 379, 383, 390, 136, 482, 365, 144, 279, 239, 397, 319, 152, 380],
    },
    {
      id: 'ivory-w', name: 'Ivory (Women)', color: '#FFFFF0', gender: 'F',
      courseRating: 69.8, slopeRating: 133,
      yardages: [301, 455, 135, 335, 275, 360, 266, 275, 120, 438, 339, 130, 258, 225, 353, 305, 115, 340],
    },
  ],
};

export const SAMPLE_COURSE: CourseSetup = buildCourseSetup(INTERNATIONAL_CC, INTERNATIONAL_CC.teeBoxes[0]);

export const HAMILTON_FARM_HIGHLANDS: CourseData = {
  id: 'hamilton-farm-highlands',
  name: 'Hamilton Farm – Highlands',
  location: 'Gladstone, NJ',
  pars: [4,5,3,4,4,4,3,4,5, 4,5,3,4,5,4,4,3,4],
  strokeIndexes: [17,1,13,7,9,3,15,11,5, 12,4,14,8,2,10,6,16,18],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 74.7, slopeRating: 139,
      yardages: [331,568,242,418,409,451,215,368,559, 381,533,212,432,545,384,465,196,431],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 71.7, slopeRating: 137,
      yardages: [323,530,188,382,403,423,193,362,515, 375,507,190,409,501,375,421,170,396],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 70.2, slopeRating: 134,
      yardages: [305,520,170,374,374,401,187,324,505, 365,483,184,377,488,353,357,160,370],
    },
    {
      id: 'white-w', name: 'White (Women)', color: '#FFFFFF', gender: 'F',
      courseRating: 72.8, slopeRating: 136,
      yardages: [305,520,170,374,374,401,187,324,505, 365,483,184,377,488,353,357,160,370],
    },
    {
      id: 'red-w', name: 'Red (Women)', color: '#CC0000', gender: 'F',
      courseRating: 71.2, slopeRating: 133,
      yardages: [285,481,147,312,341,355,155,292,460, 241,359,159,240,461,296,357,133,240],
    },
  ],
};

export const HAMILTON_FARM_HICKORY: CourseData = {
  id: 'hamilton-farm-hickory',
  name: 'Hamilton Farm – Hickory',
  location: 'Gladstone, NJ',
  pars: [3,3,3,3,3,3,3,3,3, 3,3,3,3,3,3,3,3,3],
  strokeIndexes: [3,13,17,7,11,1,5,9,15, 14,10,18,8,2,6,12,4,16],
  teeBoxes: [
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 56.4, slopeRating: 100,
      yardages: [206,148,148,174,187,218,188,171,155, 129,154,127,153,218,186,141,229,134],
    },
    {
      // Unofficial intermediate tee — not listed in GolfLink, BlueGolf, or GHIN (verified May 2026).
      // Yardages are estimated ~8% shorter than Blue; rating/slope unconfirmed.
      // Pending physical scorecard or pro-shop confirmation before treating as rated.
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 56.4, slopeRating: 98,
      yardages: [190,137,137,161,170,201,174,158,143, 119,142,117,141,201,172,130,212,124],
      unofficial: true,
    },
    {
      id: 'white-w', name: 'White (Women)', color: '#FFFFFF', gender: 'F',
      courseRating: 58.9, slopeRating: 102,
      yardages: [170,117,116,159,157,187,152,157,133, 111,126,105,123,182,166,104,191,108],
    },
  ],
};

export const KINLOCH_GOLF_CLUB: CourseData = {
  id: 'kinloch-golf-club',
  name: 'Kinloch Golf Club',
  location: 'Manakin-Sabot, VA',
  pars: [4,4,5,4,3,4,3,4,5, 4,5,4,5,3,4,4,3,4],
  strokeIndexes: [9,11,7,15,17,1,13,3,5, 6,12,4,10,18,14,2,16,8],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 76.1, slopeRating: 146,
      yardages: [447,407,617,334,194,450,237,457,586, 450,542,455,579,156,328,471,213,450],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 73.9, slopeRating: 141,
      yardages: [420,375,540,319,175,410,217,437,565, 422,521,432,540,140,324,440,203,400],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 71.9, slopeRating: 137,
      yardages: [383,340,523,310,165,385,199,407,540, 405,500,415,520,130,300,390,167,375],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 70.9, slopeRating: 135,
      yardages: [370,305,473,275,135,328,173,378,468, 362,467,405,462,124,277,360,130,350],
    },
    {
      id: 'red-w', name: 'Red (Women)', color: '#CC0000', gender: 'F',
      courseRating: 72.3, slopeRating: 134,
      yardages: [332,268,428,254,113,295,158,315,432, 325,402,374,423,111,230,330,122,310],
    },
  ],
};

export const LIGHTHOUSE_SOUND: CourseData = {
  id: 'lighthouse-sound',
  name: 'The Links at Lighthouse Sound',
  location: 'Bishopville, MD',
  pars: [4, 5, 3, 4, 4, 5, 4, 4, 3, 4, 4, 3, 4, 4, 5, 4, 3, 4],
  strokeIndexes: [13, 11, 15, 7, 9, 3, 17, 1, 5, 4, 14, 18, 6, 10, 12, 2, 16, 8],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 74.6, slopeRating: 138,
      yardages: [375, 508, 190, 430, 420, 218, 390, 622, 395, 444, 357, 210, 250, 455, 360, 325, 455, 547],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 72.2, slopeRating: 133,
      yardages: [332, 496, 173, 385, 385, 185, 385, 585, 395, 430, 350, 209, 145, 409, 161, 290, 340, 333],
    },
    {
      id: 'green', name: 'Green', color: '#228B22', gender: 'M',
      courseRating: 71.2, slopeRating: 131,
      yardages: [332, 496, 173, 385, 385, 185, 385, 585, 395, 430, 350, 209, 145, 409, 161, 290, 340, 333],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 69.8, slopeRating: 126,
      yardages: [281, 448, 162, 360, 360, 173, 350, 550, 350, 389, 317, 189, 125, 389, 146, 275, 325, 300],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 72.8, slopeRating: 136,
      yardages: [273, 439, 139, 352, 267, 112, 287, 516, 287, 365, 275, 195, 105, 367, 120, 252, 293, 233],
    },
    {
      id: 'red', name: 'Red', color: '#CC0000', gender: 'M',
      courseRating: 67.8, slopeRating: 115,
      yardages: [235, 389, 82, 235, 242, 107, 240, 405, 240, 320, 225, 175, 95, 320, 105, 222, 273, 404],
    },
  ],
};

export const BENTWINDS_CC: CourseData = {
  id: 'bentwinds-cc',
  name: 'Bentwinds Country Club',
  location: 'Fuquay-Varina, NC',
  pars: [5, 4, 3, 4, 5, 4, 4, 3, 4, 5, 3, 4, 4, 5, 3, 4, 4, 4],
  strokeIndexes: [9, 17, 11, 15, 13, 1, 7, 5, 3, 14, 16, 2, 8, 12, 18, 6, 10, 4],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#000000', gender: 'M',
      courseRating: 72.9, slopeRating: 137,
      yardages: [554, 375, 173, 354, 500, 470, 367, 191, 425, 480, 165, 411, 403, 512, 181, 432, 372, 405],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 71.3, slopeRating: 129,
      yardages: [544, 340, 169, 329, 489, 431, 344, 180, 398, 475, 148, 383, 389, 478, 159, 401, 365, 380],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 70.0, slopeRating: 122,
      yardages: [501, 330, 156, 319, 471, 406, 321, 173, 374, 457, 139, 353, 362, 454, 151, 372, 340, 355],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 67.0, slopeRating: 114,
      yardages: [466, 291, 133, 288, 461, 358, 303, 143, 336, 441, 117, 326, 324, 422, 128, 300, 332, 273],
    },
    {
      id: 'blue-w', name: 'Blue (Women)', color: '#0066CC', gender: 'F',
      courseRating: 77.2, slopeRating: 143,
      yardages: [544, 340, 169, 329, 489, 431, 344, 180, 398, 475, 148, 383, 389, 478, 159, 401, 365, 380],
    },
    {
      id: 'white-w', name: 'White (Women)', color: '#FFFFFF', gender: 'F',
      courseRating: 75.2, slopeRating: 139,
      yardages: [501, 330, 156, 319, 471, 406, 321, 173, 374, 457, 139, 353, 362, 454, 151, 372, 340, 355],
    },
    {
      id: 'gold-w', name: 'Gold (Women)', color: '#FFD700', gender: 'F',
      courseRating: 71.7, slopeRating: 127,
      yardages: [466, 291, 133, 288, 461, 358, 303, 143, 336, 441, 117, 326, 324, 422, 128, 300, 332, 273],
    },
    {
      id: 'red-w', name: 'Red (Women)', color: '#CC0000', gender: 'F',
      courseRating: 68.9, slopeRating: 120,
      yardages: [414, 267, 102, 282, 399, 292, 298, 136, 297, 390, 112, 265, 283, 417, 109, 295, 305, 242],
    },
  ],
};

// Verified scorecard for Keswick Club, Keswick VA.
// Source: physical scorecard photos (Aug 2026). All yardages verified against card totals.
// Tee names as printed: Tournament (black), Rye (blue), Keswick (white), Virginia (green), Monticello (red)
// Men's HCP front: 11,3,9,17,1,7,13,5,15  back: 10,18,6,16,12,2,14,4,8 (Women's HCP identical per card)
// Course/slope ratings not shown on physical card — estimates only; update when USGA data is confirmed.
export const KESWICK_CLUB: CourseData = {
  id: 'keswick-club',
  name: 'Keswick Club',
  location: 'Keswick, VA',
  pars: [4, 5, 4, 3, 4, 4, 3, 5, 4, 4, 3, 5, 4, 4, 4, 3, 5, 4],
  // Front: 11,3,9,17,1,7,13,5,15  Back: 10,18,6,16,12,2,14,4,8
  strokeIndexes: [11, 3, 9, 17, 1, 7, 13, 5, 15, 10, 18, 6, 16, 12, 2, 14, 4, 8],
  teeBoxes: [
    {
      id: 'tournament', name: 'Tournament', color: '#1a1a1a', gender: 'M',
      courseRating: 75.0, slopeRating: 140,
      // Front: 401,609,345,234,471,454,193,543,328=3578  Back: 364,226,588,320,327,461,243,579,467=3575  Tot:7153
      yardages: [401, 609, 345, 234, 471, 454, 193, 543, 328, 364, 226, 588, 320, 327, 461, 243, 579, 467],
    },
    {
      id: 'dye', name: 'Dye', color: '#0066CC', gender: 'M',
      courseRating: 72.0, slopeRating: 132,
      // Front: 358,526,329,206,405,395,179,519,302=3219  Back: 343,173,518,292,286,446,203,526,408=3195  Tot:6414
      yardages: [358, 526, 329, 206, 405, 395, 179, 519, 302, 343, 173, 518, 292, 286, 446, 203, 526, 408],
    },
    {
      id: 'keswick', name: 'Keswick', color: '#FFFFFF', gender: 'M',
      courseRating: 69.5, slopeRating: 125,
      // Front: 332,487,325,174,360,392,155,499,298=3022  Back: 332,155,484,273,282,410,200,523,378=3037  Tot:6059
      yardages: [332, 487, 325, 174, 360, 392, 155, 499, 298, 332, 155, 484, 273, 282, 410, 200, 523, 378],
    },
    {
      id: 'virginia', name: 'Virginia', color: '#228B22', gender: 'M',
      courseRating: 67.0, slopeRating: 118,
      // Front: 304,469,296,155,316,364,143,458,258=2763  Back: 294,141,468,259,252,359,160,466,323=2722  Tot:5485
      yardages: [304, 469, 296, 155, 316, 364, 143, 458, 258, 294, 141, 468, 259, 252, 359, 160, 466, 323],
    },
    {
      id: 'monticello', name: 'Monticello', color: '#CC0000', gender: 'M',
      courseRating: 63.5, slopeRating: 109,
      // Front: 271,405,280,119,291,312,109,408,251=2446  Back: 240,114,419,227,219,309,114,429,301=2372  Tot:4818
      yardages: [271, 405, 280, 119, 291, 312, 109, 408, 251, 240, 114, 419, 227, 219, 309, 114, 429, 301],
    },
    {
      id: 'virginia-w', name: 'Virginia (Women)', color: '#228B22', gender: 'F',
      courseRating: 72.5, slopeRating: 128,
      yardages: [304, 469, 296, 155, 316, 364, 143, 458, 258, 294, 141, 468, 259, 252, 359, 160, 466, 323],
    },
    {
      id: 'monticello-w', name: 'Monticello (Women)', color: '#CC0000', gender: 'F',
      courseRating: 68.5, slopeRating: 118,
      yardages: [271, 405, 280, 119, 291, 312, 109, 408, 251, 240, 114, 419, 227, 219, 309, 114, 429, 301],
    },
  ],
};

// Verified scorecard for Kenwood Golf & Country Club, Bethesda MD.
// Source: physical scorecard photo (July 2026). All yardages verified against card totals.
// This hardcoded entry replaces the DB/API row (id 52173902-8935-4126-8e2d-dbc2dd080498)
// which is temporarily hidden from the picker via DB_COURSE_BLOCK_LIST in SetupScreen.
//
// Tee structure matches the physical card exactly:
//   BLUE (M) · BLUE/WHITE (M, combined) · WHITE (M) · GOLD (M) · GREEN (M)
//   GREEN/SILVER (F, combined) · SILVER (F)
//
// BLUE/WHITE: holes 1,2,4,8 play White (front); holes 14,17,18 play White (back).
//   Rating/slope is a USGA-method weighted average of Blue + White certified ratings.
// GREEN/SILVER: holes 5,7 play Silver (front); holes 14,16 play Silver (back).
//   Rating/slope is a USGA-method weighted average of Green-F + Silver-F certified ratings.
//
// Men's strokeIndexes: [5,7,13,1,9,15,17,3,11, 14,18,6,10,2,12,8,16,4]
// Women's strokeIndexes (not separately stored — data model has one SI array):
//   [5,9,11,1,7,17,15,13,3, 10,18,6,12,2,16,4,8,14]
// Par Men (70): [4,3,3,4,4,3,5,4,5, 4,4,4,3,4,3,4,5,4]
// Par Women (71): same except hole 18 = 5 — known single-array limitation.
export const KENWOOD_GCC: CourseData = {
  id: 'kenwood-gcc',
  name: 'Kenwood Golf & Country Club',
  location: 'Bethesda, MD',
  pars: [4, 3, 3, 4, 4, 3, 5, 4, 5, 4, 4, 4, 3, 4, 3, 4, 5, 4],
  strokeIndexes: [5, 7, 13, 1, 9, 15, 17, 3, 11, 14, 18, 6, 10, 2, 12, 8, 16, 4],
  teeBoxes: [
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 69.9, slopeRating: 123,
      // Card: Out 3016, In 3110, Total 6126
      yardages: [345, 220, 162, 414, 355, 150, 467, 385, 518, 294, 292, 383, 197, 425, 195, 422, 462, 440],
    },
    {
      id: 'blue-white', name: 'Blue/White', color: '#6699CC', gender: 'M',
      // Approx rating: (69.9×11 + 68.6×7)/18 ≈ 69.4; slope (123×11 + 120×7)/18 ≈ 122
      courseRating: 69.4, slopeRating: 122,
      // Card: Out 2949, In 3016, Total 5965
      // Front: H1,H2,H4,H8 from White; H3,H5,H6,H7,H9 from Blue
      // Back:  H14,H17,H18 from White; H10,H11,H12,H13,H15,H16 from Blue
      yardages: [333, 201, 162, 400, 355, 150, 467, 363, 518, 294, 292, 383, 197, 382, 195, 422, 421, 430],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 68.6, slopeRating: 120,
      // Card: Out 2888, In 2929, Total 5817
      yardages: [333, 201, 151, 400, 348, 139, 447, 363, 506, 283, 284, 373, 180, 382, 168, 408, 421, 430],
    },
    {
      id: 'gold', name: 'Gold', color: '#FFD700', gender: 'M',
      courseRating: 67.4, slopeRating: 118,
      // Card: Out 2759, In 2710, Total 5469
      yardages: [321, 182, 140, 383, 341, 139, 440, 350, 463, 283, 284, 345, 140, 363, 151, 354, 405, 385],
    },
    {
      id: 'green-m', name: 'Green', color: '#15803D', gender: 'M',
      courseRating: 66.0, slopeRating: 113,
      // Card: Out 2625, In 2629, Total 5254
      yardages: [279, 182, 140, 383, 341, 127, 440, 270, 463, 272, 231, 345, 140, 363, 134, 354, 405, 385],
    },
    {
      id: 'green-silver-w', name: 'Green/Silver', color: '#5B7A5B', gender: 'F',
      // Approx rating: (71.0×14 + 66.5×4)/18 ≈ 70.0; slope (125×14 + 108×4)/18 ≈ 121
      courseRating: 70.0, slopeRating: 121,
      // Card: Out 2451, In 2492, Total 4943
      // Front: H5,H7 from Silver; H1,H2,H3,H4,H6,H8,H9 from Green
      // Back:  H14,H16 from Silver; H10,H11,H12,H13,H15,H17,H18 from Green
      yardages: [279, 182, 140, 383, 262, 127, 345, 270, 463, 272, 231, 345, 140, 295, 134, 285, 405, 385],
    },
    {
      id: 'silver-w', name: 'Silver', color: '#C0C0C0', gender: 'F',
      courseRating: 66.5, slopeRating: 108,
      // Card: Out 2184, In 2252, Total 4436
      yardages: [279, 105, 128, 270, 262, 127, 345, 270, 398, 272, 231, 297, 140, 295, 88, 285, 300, 344],
    },
  ],
};

// ─── Verified Course Registry ────────────────────────────────────────────────
//
// Rule: when the owner provides a current physical scorecard and designates it
// authoritative, the manually verified data supersedes Golf Course API data for
// ALL future rounds.  Historical rounds are permanently tied to the
// courseSnapshotJson stored when that round was created and are unaffected by
// any later change to these entries.
//
// An API course that resolves to the same physical course as a verified entry
// must be suppressed from the picker and rejected at import (HTTP 409).
// Courses at a different location sharing a similar name are NOT suppressed.
// When location data is incomplete or ambiguous the API result is surfaced as
// ambiguous rather than silently suppressed.
//
// To add a new verified course: append to VERIFIED_COURSE_REGISTRY and list
// all known name variants the Golf Course API might return.

export interface VerifiedCourseIdentity {
  /** Matches the id field in AVAILABLE_COURSE_DATA */
  courseId: string;
  /** Every normalized name the external API might return for this course */
  nameAliases: string[];
  /** Canonical city (lowercase). Empty string = not used in matching. */
  city: string;
  /** Full state/province name, lowercase */
  state: string;
  /** Additional abbreviations / short-forms accepted for state */
  stateVariants: string[];
  /** Country name, lowercase */
  country: string;
  /** Additional country aliases */
  countryVariants: string[];
  /** Known opaque Golf Course API IDs for this physical course (if any) */
  knownApiIds: string[];
}

export const VERIFIED_COURSE_REGISTRY: VerifiedCourseIdentity[] = [
  {
    courseId: 'keswick-club',
    // Physical card verified Aug 2026.  The API may return this club under
    // "Keswick Golf Club" or just "Keswick"; all map to the same Virginia course.
    nameAliases: ['keswick club', 'keswick golf club', 'keswick'],
    city: 'keswick',
    state: 'virginia',
    stateVariants: ['va'],
    country: 'united states',
    countryVariants: ['usa', 'us', 'united states of america'],
    knownApiIds: [],
  },
  {
    courseId: 'ocean-city-newport-bay',
    nameAliases: ['ocean city golf club', 'ocean city golf club newport bay', 'ocean city gc'],
    city: 'ocean city',
    state: 'maryland',
    stateVariants: ['md'],
    country: 'united states',
    countryVariants: ['usa', 'us', 'united states of america'],
    knownApiIds: [],
  },
  {
    courseId: 'rum-pointe',
    // Physical card verified Aug 2026.
    nameAliases: ['rum pointe', 'rum pointe seaside golf links', 'rum pointe golf links', 'rum pointe golf course'],
    city: 'berlin',
    state: 'maryland',
    stateVariants: ['md'],
    country: 'united states',
    countryVariants: ['usa', 'us', 'united states of america'],
    knownApiIds: [],
  },
];

/** Normalise a string for comparison: lowercase, strip punctuation, collapse spaces. */
function normalizeForMatch(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ');
}

export type VerifiedCourseMatchResult =
  | { status: 'match';     courseId: string }
  | { status: 'no_match' }
  | { status: 'ambiguous'; reason: string };

/**
 * Check whether an external API search result refers to the same physical course
 * as a verified hardcoded entry.
 *
 * Returns:
 *   'match'    — same physical course; suppress from picker / reject import.
 *   'no_match' — different course; leave importable.
 *   'ambiguous'— name matches but location data is incomplete; surface to user.
 */
export function checkApiResultAgainstRegistry(
  clubName: string,
  courseName: string | undefined | null,
  location: { city?: string; state?: string; country?: string } | undefined | null,
  apiId?: string | null,
): VerifiedCourseMatchResult {
  const normalizedClub   = normalizeForMatch(clubName ?? '');
  const normalizedCourse = normalizeForMatch(courseName ?? '');

  for (const entry of VERIFIED_COURSE_REGISTRY) {
    // 1. Check exact provider API IDs first — most reliable signal.
    if (apiId && entry.knownApiIds.includes(apiId)) {
      return { status: 'match', courseId: entry.courseId };
    }

    // 2. Name match: club_name ONLY — never course_name.
    //    course_name is a sub-course designation (e.g. "Dye", "Old Course") and
    //    must not be used for registry matching because an unrelated club (e.g.
    //    "Keswick Hall") may legitimately have a course called "Keswick", which
    //    would produce a false suppression.
    const nameMatch = entry.nameAliases.some((alias) => {
      const norm = normalizeForMatch(alias);
      return normalizedClub === norm;
    });
    if (!nameMatch) continue;

    // 3. Name matched — verify location to distinguish same-named courses worldwide.
    const apiCity    = normalizeForMatch(location?.city    ?? '');
    const apiState   = normalizeForMatch(location?.state   ?? '');
    const apiCountry = normalizeForMatch(location?.country ?? '');
    const hasLocation = apiCity || apiState || apiCountry;

    if (!hasLocation) {
      return { status: 'ambiguous', reason: 'name matches a verified course but no location data was returned' };
    }

    const cityMatch = !apiCity || !entry.city ||
      apiCity === normalizeForMatch(entry.city);

    const stateMatch = !apiState || !entry.state || (() => {
      const norm = normalizeForMatch(entry.state);
      return apiState === norm || entry.stateVariants.map(v => normalizeForMatch(v)).includes(apiState);
    })();

    const countryMatch = !apiCountry || !entry.country || (() => {
      const norm = normalizeForMatch(entry.country);
      return apiCountry === norm || entry.countryVariants.map(v => normalizeForMatch(v)).includes(apiCountry);
    })();

    if (cityMatch && stateMatch && countryMatch) {
      return { status: 'match', courseId: entry.courseId };
    }
    // Name matched but location doesn't → a different physical course with the same name.
  }

  return { status: 'no_match' };
}

// Verified scorecard for Ocean City Golf Club — Newport Bay course, Ocean City MD.
// Source: physical scorecard photo (Aug 2026).
// Pars and stroke indexes are exact from the card.
// Yardages: Red/White/Blue verified against card totals; Black h6 corrected from
//   apparent image misread (345→545); 4-yd residual gap in Black front noted below.
//
// Men's HDCP front:  11,15,3,5,13,1,17,9,7
// Men's HDCP back:    6,8,4,14,2,10,16,18,12
// Course/Slope ratings as printed on card.
export const OCEAN_CITY_NEWPORT_BAY: CourseData = {
  id: 'ocean-city-newport-bay',
  name: 'Ocean City Golf Club — Newport Bay',
  location: 'Ocean City, MD',
  pars: [4, 3, 4, 4, 4, 5, 3, 4, 5, 4, 4, 5, 3, 5, 4, 4, 3, 4],
  strokeIndexes: [11, 15, 3, 5, 13, 1, 17, 9, 7, 6, 8, 4, 14, 2, 10, 16, 18, 12],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#1a1a1a', gender: 'M',
      courseRating: 71.0, slopeRating: 126,
      // Front: 384,182,444,342,342,545,173,401,472≈3285 (scorecard OUT 3289, -4 diff)
      // Back:  433,338,520,216,535,440,349,178,359=3368  Tot: scorecard 6657
      yardages: [384, 182, 444, 342, 342, 545, 173, 401, 472, 433, 338, 520, 216, 535, 440, 349, 178, 359],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      courseRating: 69.4, slopeRating: 119,
      // Front: 367,170,426,329,334,512,149,385,453=3125  Back: 414,321,487,199,520,405,329,172,340=3187  Tot:6312
      // (h6=512 confirmed from physical card; 5-yd gap vs printed 6307 is one other unresolved digit)
      yardages: [367, 170, 426, 329, 334, 512, 149, 385, 453, 414, 321, 487, 199, 520, 405, 329, 172, 340],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 67.5, slopeRating: 113,
      // Front: 349,159,395,319,323,496,136,369,441=2987  Back: 397,304,472,177,509,390,309,165,315=3038  Tot:6025 ✓
      yardages: [349, 159, 395, 319, 323, 496, 136, 369, 441, 397, 304, 472, 177, 509, 390, 309, 165, 315],
    },
    {
      id: 'red', name: 'Red', color: '#CC0000', gender: 'F',
      courseRating: 71.5, slopeRating: 119,
      // Front: 305,132,361,290,293,403,106,351,407=2648  Back: 344,255,374,139,470,349,272,125,229=2557  Tot:5205 ✓
      yardages: [305, 132, 361, 290, 293, 403, 106, 351, 407, 344, 255, 374, 139, 470, 349, 272, 125, 229],
    },
  ],
};

// Verified scorecard for Rum Pointe Seaside Golf Links, Berlin MD.
// Source: physical scorecard photo (Aug 2026). All four tees verified against
// card totals — every tee OUT/IN/TOT matches exactly.
//
// Men's HDCP front:  7,13,5,17,15,11,1,3,9
// Men's HDCP back:   4,18,14,10,6,12,2,16,8
// White tee carries dual ratings: Men's 68.0/113 and Ladies' 74.6/129.
export const RUM_POINTE: CourseData = {
  id: 'rum-pointe',
  name: 'Rum Pointe Seaside Golf Links',
  location: 'Berlin, MD',
  pars: [4, 3, 5, 4, 3, 4, 4, 5, 4, 5, 4, 4, 5, 3, 4, 4, 3, 4],
  strokeIndexes: [7, 13, 5, 17, 15, 11, 1, 3, 9, 4, 18, 14, 10, 6, 12, 2, 16, 8],
  teeBoxes: [
    {
      id: 'black', name: 'Black', color: '#1a1a1a', gender: 'M',
      courseRating: 72.6, slopeRating: 122,
      // Front: 422,177,509,393,175,398,471,540,419=3504  Back: 505,369,400,501,223,407,463,185,444=3497  Tot:7001 ✓
      yardages: [422, 177, 509, 393, 175, 398, 471, 540, 419, 505, 369, 400, 501, 223, 407, 463, 185, 444],
    },
    {
      id: 'blue', name: 'Blue', color: '#0066CC', gender: 'M',
      // GHIN-verified Blue rating/slope. The supplied card shows 70.4/118,
      // but GHIN is authoritative for the handicap calculation at this course.
      courseRating: 71.5, slopeRating: 118,
      // Front: 396,153,484,367,153,372,434,517,396=3272  Back: 485,346,357,481,203,384,425,158,412=3251  Tot:6523 ✓
      yardages: [396, 153, 484, 367, 153, 372, 434, 517, 396, 485, 346, 357, 481, 203, 384, 425, 158, 412],
    },
    {
      id: 'white', name: 'White', color: '#FFFFFF', gender: 'M',
      courseRating: 68.0, slopeRating: 113,
      // Front: 365,138,456,332,127,356,403,489,381=3047  Back: 468,330,324,415,176,356,375,127,386=2957  Tot:6004 ✓
      yardages: [365, 138, 456, 332, 127, 356, 403, 489, 381, 468, 330, 324, 415, 176, 356, 375, 127, 386],
    },
    {
      id: 'white-w', name: 'White (Women)', color: '#FFFFFF', gender: 'F',
      courseRating: 74.6, slopeRating: 129,
      yardages: [365, 138, 456, 332, 127, 356, 403, 489, 381, 468, 330, 324, 415, 176, 356, 375, 127, 386],
    },
    {
      id: 'red', name: 'Red', color: '#CC0000', gender: 'F',
      courseRating: 70.3, slopeRating: 120,
      // Front: 318,125,421,281,100,301,354,438,356=2694  Back: 432,279,272,380,157,325,313,101,323=2582  Tot:5276 ✓
      yardages: [318, 125, 421, 281, 100, 301, 354, 438, 356, 432, 279, 272, 380, 157, 325, 313, 101, 323],
    },
  ],
};

// ─── Available Course Data ────────────────────────────────────────────────────
//
// Verified/manual course data outranks Golf Course API data for future rounds.
// Historical rounds use their stored courseSnapshotJson and are not affected by
// later changes to these course templates.
export const AVAILABLE_COURSE_DATA: CourseData[] = [
  INTERNATIONAL_CC,
  BENTWINDS_CC,
  BLUE_MASH,
  KENWOOD_GCC,
  TPC_POTOMAC,
  WE_KO_PA,
  SILVERLEAF,
  SHEEP_RANCH,
  RTJ_GOLF_CLUB,
  KESWICK_CLUB,
  OCEAN_CITY_NEWPORT_BAY,
  RUM_POINTE,
  OLD_MACDONALD,
  BANDON_TRAILS,
  PACIFIC_DUNES,
  BANDON_DUNES,
  HAMILTON_FARM_HIGHLANDS,
  HAMILTON_FARM_HICKORY,
  KINLOCH_GOLF_CLUB,
  LIGHTHOUSE_SOUND,
];

export const AVAILABLE_COURSES: CourseSetup[] = AVAILABLE_COURSE_DATA.map(
  course => buildCourseSetup(course, course.teeBoxes[0])
);

export async function fetchCourseData(
  courseDataId: string | null,
): Promise<CourseData | null> {
  if (courseDataId) {
    const byId = AVAILABLE_COURSE_DATA.find((c) => c.id === courseDataId);
    if (byId) return byId;
  }
  return null;
}

function buildHolesFromCourse(courseData: CourseData, teeBox?: TeeBox): HoleInfo[] {
  const activeTee = teeBox ?? courseData.teeBoxes[0];
  return courseData.pars.map((par, i) => ({
    number: i + 1,
    par,
    yardage: activeTee?.yardages[i] ?? 0,
    strokeIndex: courseData.strokeIndexes[i],
  }));
}

function buildFallbackHoles(): HoleInfo[] {
  return Array.from({ length: 18 }, (_, i) => ({
    number: i + 1,
    par: 4,
    yardage: 0,
    strokeIndex: i + 1,
  }));
}

export async function hydrateGameStateFromRound(
  apiResponse: BackendRoundResponse
): Promise<GameState> {
  const { round, scores: backendScores, presses: backendPresses } = apiResponse;

  let holes: HoleInfo[];
  if (round.courseSnapshotJson && Array.isArray((round.courseSnapshotJson as any).holes)) {
    const snap = round.courseSnapshotJson as any;
    holes = snap.holes.map((h: any) => ({
      number: h.number ?? h.holeNumber,
      par: h.par,
      yardage: h.yardage ?? 0,
      strokeIndex: h.strokeIndex ?? h.handicapIndex,
    }));
  } else {
    const courseData = await fetchCourseData(round.courseDataId);
    if (courseData) {
      const teamsJson = Array.isArray(round.teamsJson) ? round.teamsJson as Team[] : [];
      const firstTeeId = teamsJson[0]?.players?.[0]?.teeBoxId;
      const selectedTee = firstTeeId ? courseData.teeBoxes.find(t => t.id === firstTeeId) : undefined;
      holes = buildHolesFromCourse(courseData, selectedTee);
    } else {
      console.warn(`[hydrateGameState] No course data found for courseDataId="${round.courseDataId}". Using fallback.`);
      holes = buildFallbackHoles();
    }
  }

  let teams: Team[];
  if (Array.isArray(round.teamsJson)) {
    teams = round.teamsJson;
  } else if (round.teamsJson && typeof round.teamsJson === 'object' && ('A' in round.teamsJson || 'B' in round.teamsJson)) {
    const obj = round.teamsJson as { A?: any[], B?: any[] };
    teams = [
      {
        id: 'team-a',
        players: (obj.A ?? []).map((p: any) => ({
          id: p.id ?? `team-a-p${p.position ?? 0}`,
          name: p.displayName ?? p.name ?? 'Player',
          handicap: p.handicapIndex ?? p.handicap ?? 0,
          position: p.position ?? 0,
        }))
      },
      {
        id: 'team-b',
        players: (obj.B ?? []).map((p: any) => ({
          id: p.id ?? `team-b-p${p.position ?? 0}`,
          name: p.displayName ?? p.name ?? 'Player',
          handicap: p.handicapIndex ?? p.handicap ?? 0,
          position: p.position ?? 0,
        }))
      }
    ];
  } else {
    teams = [{ id: 'team-a', players: [] }, { id: 'team-b', players: [] }];
  }

  if (
    !Array.isArray(teams) ||
    teams.length === 0 ||
    teams.some(t => !t.id || !Array.isArray(t.players) || t.players.length === 0)
  ) {
    const snapshot = JSON.stringify(teams);
    throw new Error(
      `[hydrateGameStateFromRound] Invalid teams after parsing teamsJson for round ${round.id}. ` +
      `Expected non-empty Team[] with populated players, got: ${snapshot.slice(0, 200)}`
    );
  }

  for (const team of teams) {
    for (const player of team.players) {
      if (!player.id || typeof player.name !== 'string' || !player.name) {
        throw new Error(
          `[hydrateGameStateFromRound] Invalid player in team "${team.id}" for round ${round.id}: ` +
          `missing id or name. Got: ${JSON.stringify(player).slice(0, 150)}`
        );
      }
    }
  }

  const startingHole = (round.startingHole === 10 ? 10 : 1) as 1 | 10;
  const gameMode = round.gameMode as GameMode;

  const scoreInputs: import('@shared/gameTypes').HoleScoreInput[] = backendScores
    .filter(s => s.grossScore !== null)
    .map(s => ({ playerId: s.playerId, holeNumber: s.holeNumber, grossScore: s.grossScore, netScore: s.netScore }));

  const manualGameEvents: import('@shared/gameTypes').ManualGameEvent[] =
    gameMode === 'TwoDownAuto'
      ? []
      : backendPresses.map(p => ({
          id: p.id,
          type: 'press' as const,
          betType: p.betType as BetType,
          startingHole: p.startingHole,
          initiatedBy: p.initiatedBy as 'top' | 'bottom',
          stackCount: p.stackCount,
          parentPressId: p.parentPressId,
        }));

  const roundConfig: import('@shared/gameTypes').RoundConfig = {
    gameType: 'nassau',
    matchFormat: round.matchFormat as MatchFormat,
    gameMode,
    teams,
    holes,
    stakes: {
      front: round.stakeFront,
      back: round.stakeBack,
      overall: round.stakeOverall,
    },
    startingHole,
    courseDataId: round.courseDataId ?? undefined,
    roundId: round.id,
    isFinalized: round.isFinalized,
  };

  const { getGameEngine } = await import('@/games/index');
  const engine = getGameEngine('nassau');
  const { gameState } = engine.rebuildRoundState({
    roundConfig,
    scores: scoreInputs,
    manualGameEvents,
  });

  return gameState;
}
